import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import _ from 'underscore';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Label, TextField } from '@fluentui/react';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { helpbuttonStyle, convertToLocalStyle } from '@bingads-webui-campaign-react/common-styles';
import { UserContext } from '@bingads-webui-react/user-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { siteMapPropType, withSiteMap, findSiteMap, getSiteMapItemRoute, processorSet } from '@bingads-webui-campaign-react/with-site-map';
import { BrandsPicker } from '../brands-picker';

const getStyles = (useless, { palette = {} }) => ({
  Form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    margin: '16px 0',
    '& .list-name-length': {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: '2px 0',
    },
    ...convertToLocalStyle(helpbuttonStyle({ palette })),
  },
});

const LIST_NAME_MAX_LENGTH = 128;

export const ManageBrandListPanelContentImpl = ({
  unscopedSiteMap, brandListId, name, onNameChange, isConfirmDialogOn, onOpenRequestBrandPanel,
}) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const [listName, setListName] = useState(name);

  const onChangeListName = useCallback((e, value) => {
    if (value.length <= LIST_NAME_MAX_LENGTH) {
      onNameChange(value);
      setListName(value);
    }
  }, [onNameChange]);

  const userContext = useContext(UserContext);
  const { scope } = useContext(ScopeContext);
  const brandListsPageLink = useMemo(() => {
    const siteMapItemId = 'brand-lists';
    const siteMapItem = findSiteMap(unscopedSiteMap, item => item.id === siteMapItemId);
    return getSiteMapItemRoute({ item: siteMapItem, scope, userContext }).href;
  }, [unscopedSiteMap, userContext, scope]);

  return (
    <div className={classNames(classes.Form, 'list-content')}>
      {!brandListId && (
        <>
          {isConfirmDialogOn && (
            <div className={classNames(classes.FormItem, 'list-description')}>
              <ReactTemplate
                model={{
                  link: text => (<Link target="_blank" to={brandListsPageLink}>{text}</Link>),
                }}
                template={i18n.getString(_TL_('Once you create a brand list, you can edit it in your {{link}}Brand List{{/link}} shared library.'))}
              />
            </div>
          )}
          <div className={classNames(classes.FormItem, 'list-name')}>
            <Label htmlFor="list_name">
              {i18n.getString(_TL_('List name'))}
              <HelpPopup topic="pop_BA_BrandLists_Name" aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('List name')) })} />
            </Label>
            <TextField
              id="list_name"
              className="list-name-input"
              placeholder={i18n.getString(_TL_('Enter your list name'))}
              value={listName}
              onChange={onChangeListName}
            />
            <p className="list-name-length">
              {i18n.getString(_TL_('{{count}} characters remaining'), {
                count: LIST_NAME_MAX_LENGTH - listName.length,
              })}
            </p>
          </div>
        </>
      )}
      <div className={classNames(classes.FormItem, 'list-brands')}>
        <Label htmlFor="brands">
          {i18n.getString(_TL_('Brands'))}
          <HelpPopup topic="pop_BA_BrandLists_Brands" aria-label={i18n.getString(_TL_('Click to learn more about {{item}}'), { item: i18n.getString(_TL_('Brands')) })} />
        </Label>
        <BrandsPicker onRequestBrand={onOpenRequestBrandPanel} />
      </div>
    </div>
  );
};

ManageBrandListPanelContentImpl.propTypes = {
  unscopedSiteMap: siteMapPropType.isRequired,
  brandListId: PropTypes.number,
  name: PropTypes.string,
  onNameChange: PropTypes.func.isRequired,
  isConfirmDialogOn: PropTypes.bool,
  onOpenRequestBrandPanel: PropTypes.func.isRequired,
};

ManageBrandListPanelContentImpl.defaultProps = {
  name: '',
  brandListId: undefined,
  isConfirmDialogOn: false,
};

const scopeUnawareWithSiteMap = _.partial(withSiteMap, _, processorSet.basic, _, _, 'unscopedSiteMap');
export const ManageBrandListPanelContent = scopeUnawareWithSiteMap(ManageBrandListPanelContentImpl);
